import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled';

import { useAnalytics } from '../../../common/controller/analytics/use-analytics';

import { type ConversationStarter, ConversationStarters } from './conversation-starters';
import { isEnglishLocale } from './is-english-locale';
import { onConvoStarterClick } from './on-convo-starter-click';
import { onConvoStartersFailed } from './on-convo-starters-failed';
import { onConvoStartersShown } from './on-convo-starters-shown';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isEnglishLocale } from './is-english-locale';

export type ConversationStartersBannerProps = {
	primaryProduct: string | undefined;
	searchResult: {
		ari: string;
		url: string;
	};
	cloudId: string;
	analyticsAttributes: any;
};

type ConversationStartersState = {
	conversationStarters: Array<{ starter: string }>;
	loading: boolean;
};

const getConversationStarters = async ({
	ari,
	product,
	cloudId,
}: {
	ari: string;
	product: string;
	cloudId: string;
}) => {
	const requestBody = {
		ai_feature_input: {
			documents: [{ ari }],
		},
	};

	const response = await fetch('/gateway/api/assist/api/ai/v2/ai-feature/convo-starters', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Experience-Id': 'convo-starter-for-search',
			'X-Product': product,
			'X-Cloudid': cloudId,
		},
		body: JSON.stringify(requestBody),
	});

	if (!response.ok) {
		throw new Error(`HTTP ${response.status}: ${response.statusText}`);
	}

	return await response.json();
};

export const ConversationStartersBannerComponent = ({
	primaryProduct,
	cloudId,
	searchResult,
	analyticsAttributes,
}: ConversationStartersBannerProps): React.JSX.Element => {
	const [{ conversationStarters, loading }, setConversationStarters] =
		useState<ConversationStartersState>(() => {
			return {
				conversationStarters: [],
				loading: false,
			};
		});
	const [fireAnalyticsEvent] = useAnalytics();

	const memoizedAnalyticsAttributes = useMemo(() => analyticsAttributes, [analyticsAttributes]);

	const handleConversationStarterClick = useCallback(
		(starter: ConversationStarter, index: number) => {
			fireAnalyticsEvent(
				onConvoStarterClick({
					starter: starter,
					analyticsAttributes: memoizedAnalyticsAttributes,
					starterIndex: index,
				}),
			);
		},
		[fireAnalyticsEvent, memoizedAnalyticsAttributes],
	);

	const handleConversationStartersShown = useCallback(
		(starters: string[]) => {
			const startersWithSummarize = ['Summarize', ...starters];
			fireAnalyticsEvent(
				onConvoStartersShown({
					starters: startersWithSummarize,
					analyticsAttributes: memoizedAnalyticsAttributes,
				}),
			);
		},
		[fireAnalyticsEvent, memoizedAnalyticsAttributes],
	);

	const handleConversationStartersFailed = useCallback(
		(error: Error) => {
			fireAnalyticsEvent(
				onConvoStartersFailed({
					analyticsAttributes: memoizedAnalyticsAttributes,
					error: error.message,
				}),
			);
		},
		[fireAnalyticsEvent, memoizedAnalyticsAttributes],
	);

	useEffect(() => {
		if (!searchResult?.ari || conversationStarters.length > 0 || loading) {
			return;
		}
		setConversationStarters((prevState) => ({ ...prevState, loading: true }));
		getConversationStarters({
			ari: searchResult.ari,
			product: primaryProduct ?? '',
			cloudId: cloudId ?? '',
		})
			.then((response) => {
				setConversationStarters({
					conversationStarters: response.ai_feature_output?.results[0]?.convo_starters || [],
					loading: false,
				});
				handleConversationStartersShown(
					response.ai_feature_output?.results[0]?.convo_starters.map(
						(starter: { starter: string }) => starter.starter,
					) || [],
				);
			})
			.catch((error) => {
				setConversationStarters({
					conversationStarters: [],
					loading: false,
				});
				handleConversationStartersFailed(error);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box>
			<ConversationStarters
				conversationStarters={conversationStarters || []}
				onConversationStarterClick={handleConversationStarterClick}
				url={searchResult.url}
			/>
		</Box>
	);
};

export const ConversationStartersBanner = ({
	primaryProduct,
	cloudId,
	searchResult,
	analyticsAttributes,
}: ConversationStartersBannerProps): React.JSX.Element | null => {
	const { locale } = useIntl();
	if (!isEnglishLocale(locale)) {
		return null;
	}
	return (
		<ConversationStartersBannerComponent
			primaryProduct={primaryProduct}
			cloudId={cloudId}
			searchResult={searchResult}
			analyticsAttributes={analyticsAttributes}
		/>
	);
};
